// @flow
import * as React from "react";
import { useParams } from "react-router-dom";
import {
  SuspenseWithPerf,
  useFirestore,
  useFirestoreDocData,
  useFirestoreDocDataOnce,
} from "reactfire";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Helmet from "react-helmet";
import Skeleton from "@material-ui/lab/Skeleton";
import TicketReservationHeader from "./TicketReservation/TicketReservationHeader";
import gameSelectors from "../selectors/gameSelectors";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import { useSnackbar } from "notistack";
import { useState } from "react";

function TickerAdmin() {
  const { gameId } = useParams();
  const firestore = useFirestore();
  const [ts, setTs] = useState();
  const { enqueueSnackbar } = useSnackbar();

  const gameRef = firestore.collection("games").doc(gameId);
  const game = useFirestoreDocData(gameRef);
  const opponent = useFirestoreDocData(
    firestore.collection("opponents").doc(game.opponentId)
  );
  const team = useFirestoreDocDataOnce(
    firestore.collection("teams").doc(game.teamId)
  );

  const {
    tickerSetHome = 0,
    tickerSetGuest = 0,
    tickerPointHome = 0,
    tickerPointGuest = 0,
  } = game;

  const { leftTeam, rightTeam } = gameSelectors.getLeftAndRightTeams(
    game,
    team,
    opponent
  );

  const resetPoints = async () =>
    await gameRef
      .set(
        {
          tickerPointHome: 0,
          tickerPointGuest: 0,
        },
        { merge: true }
      )
      .catch((error) => {
        console.error(error);
        enqueueSnackbar(error.message || error, { variant: "error" });
      });

  const changePoint = async (home: boolean, increase: boolean, set: number) => {
    const values = home
      ? { tickerPointHome: tickerPointHome + 1 - (increase ? 0 : 2) }
      : { tickerPointGuest: tickerPointGuest + 1 - (increase ? 0 : 2) };
    return await gameRef.set({ ...values }, { merge: true }).catch((error) => {
      console.error(error);
      enqueueSnackbar(error.message || error, { variant: "error" });
    });
  };

  const changeSet = async (home: boolean, increase: boolean) => {
    const values = home
      ? { tickerSetHome: tickerSetHome + 1 - (increase ? 0 : 2) }
      : { tickerSetGuest: tickerSetGuest + 1 - (increase ? 0 : 2) };
    return await gameRef.set({ ...values }, { merge: true }).catch((error) => {
      console.error(error);
      enqueueSnackbar(error.message || error, { variant: "error" });
    });
  };

  const homeTeam = {
    title: (
      <Typography variant="subtitle2" align="center">
        SG Coesfeld
      </Typography>
    ),
    sets: (
      <Grid item xs={6}>
        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={() => {
            changeSet(true, true).then(() => Promise.resolve());
          }}
        >
          +
        </Button>{" "}
        <Button
          color="secondary"
          variant="contained"
          size="large"
          onClick={() => {
            changeSet(true, false).then(() => Promise.resolve());
          }}
        >
          -
        </Button>
      </Grid>
    ),
    points: (
      <Grid item xs={6}>
        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={() => {
            changePoint(true, true).then(() => Promise.resolve());
          }}
        >
          +
        </Button>{" "}
        <Button
          color="secondary"
          variant="contained"
          size="large"
          onClick={() => {
            changePoint(true, false).then(() => Promise.resolve());
          }}
        >
          -
        </Button>
      </Grid>
    ),
    setsCurrent: tickerSetHome,
    pointsCurrent: tickerPointHome,
  };

  const guestTeam = {
    title: (
      <Typography variant="subtitle2" align="center">
        {opponent.name}
      </Typography>
    ),
    sets: (
      <Grid item xs={6}>
        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={() => {
            changeSet(false, true).then(() => Promise.resolve());
          }}
        >
          +
        </Button>{" "}
        <Button
          color="secondary"
          variant="contained"
          size="large"
          onClick={() => {
            changeSet(false, false).then(() => Promise.resolve());
          }}
        >
          -
        </Button>
      </Grid>
    ),
    points: (
      <Grid item xs={6}>
        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={() => {
            changePoint(false, true).then(() => Promise.resolve());
          }}
        >
          +
        </Button>{" "}
        <Button
          color="secondary"
          variant="contained"
          size="large"
          onClick={() => {
            changePoint(false, false).then(() => Promise.resolve());
          }}
        >
          -
        </Button>
      </Grid>
    ),
    setsCurrent: tickerSetGuest,
    pointsCurrent: tickerPointGuest,
  };

  const leftTeamDisplay = ts ? guestTeam : homeTeam;
  const rightTeamDisplay = !ts ? guestTeam : homeTeam;

  return (
    <>
      <Helmet>
        <title>Ticker Admin – SG Coesfeld 06</title>
      </Helmet>
      <SuspenseWithPerf
        traceId="TickerAdminHeader"
        fallback={<Skeleton width="100%" height={30} />}
      >
        <TicketReservationHeader
          game={game}
          leftTeam={leftTeam}
          rightTeam={rightTeam}
        />
      </SuspenseWithPerf>
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid item xs={6}>
            {leftTeamDisplay.title}
          </Grid>
          <Grid item xs={6}>
            {rightTeamDisplay.title}
          </Grid>
        </Grid>
        <Box mt={5} mb={2}>
          <Typography>
            Sätze: {leftTeamDisplay.setsCurrent}:{rightTeamDisplay.setsCurrent}
          </Typography>
        </Box>
        <Grid container spacing={1}>
          {leftTeamDisplay.sets}
          {rightTeamDisplay.sets}
        </Grid>
        <Box mt={5} mb={2}>
          <Typography>
            Punkte: {leftTeamDisplay.pointsCurrent}:
            {rightTeamDisplay.pointsCurrent}
          </Typography>
        </Box>
        <Grid container spacing={1}>
          {leftTeamDisplay.points}
          {rightTeamDisplay.points}
        </Grid>
        <Box mt={2} mb={2}>
          <Button variant="contained" onClick={resetPoints}>
            Punkte 0:0
          </Button>{" "}
          <Button variant="contained" onClick={() => setTs(!ts)}>
            Seiten wechseln
          </Button>
        </Box>
      </Container>
    </>
  );
}

export default TickerAdmin;
